import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../../ui/Icon/Icon';
import DisplayTimePicker from '../DisplayTimePicker/DisplayTimePicker';
import { WEEKDAYS, type ScheduleSlot } from '../../../data/types';
import './MediaScheduler.css';

interface MediaSchedulerProps {
  schedule: ScheduleSlot[];
  onChange: (schedule: ScheduleSlot[]) => void;
  /** Fires whenever the overlap check result changes, so the parent form can block Create/Save. */
  onValidityChange?: (valid: boolean) => void;
}

const T3 = 'var(--t3, #9CA3B8)';
const ACCENT = 'var(--accent, #D23A3B)';

const DAY_SHORT: Record<string, string> = {
  Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun',
};

let slotIdCounter = 0;
function nextSlotId() { slotIdCounter += 1; return `mslot-${slotIdCounter}`; }

interface LocalSlot {
  id: string;
  day: string;
  start: string;
  end: string;
}

function groupByTime(schedule: ScheduleSlot[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const slot of schedule) {
    const key = `${slot.start_time}__${slot.end_time}`;
    const existing = map.get(key);
    if (existing) existing.add(slot.weekday);
    else map.set(key, new Set([slot.weekday]));
  }
  return map;
}

function deriveLocalSlots(schedule: ScheduleSlot[]): LocalSlot[] {
  const result: LocalSlot[] = [];
  for (const [key, daySet] of groupByTime(schedule)) {
    const [start, end] = key.split('__');
    if (daySet.size === WEEKDAYS.length) {
      result.push({ id: nextSlotId(), day: 'all', start, end });
    } else {
      for (const day of daySet) result.push({ id: nextSlotId(), day, start, end });
    }
  }
  return result;
}

function expandSlot(slot: LocalSlot): ScheduleSlot[] {
  if (slot.day === 'all') return WEEKDAYS.map((weekday) => ({ weekday, start_time: slot.start, end_time: slot.end }));
  return [{ weekday: slot.day, start_time: slot.start, end_time: slot.end }];
}

function slotDaysExpanded(slot: LocalSlot): string[] {
  return slot.day === 'all' ? [...WEEKDAYS] : [slot.day];
}

function timeToMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

function rangesForSlot(start: string, end: string): Array<{ start: number; end: number }> {
  const startMin = timeToMinutes(start);
  const endMin = timeToMinutes(end);
  if (endMin > startMin) return [{ start: startMin, end: endMin }];
  // Overnight slot: split at midnight so overlap checks still work.
  return [
    { start: startMin, end: 24 * 60 },
    { start: 0, end: endMin },
  ];
}

function rangesOverlap(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
  return a.start < b.end && b.start < a.end;
}

/** End must be strictly after start — mirrors Product Catalog's Session scheduler validation. */
function getRangeErrors(slots: LocalSlot[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const slot of slots) {
    if (timeToMinutes(slot.start) >= timeToMinutes(slot.end)) {
      out[slot.id] = 'End time must be greater than start time.';
    }
  }
  return out;
}

/** Same-day, same-time-range collision check across slots — mirrors Product Catalog's Session scheduler. */
function getOverlapErrors(slots: LocalSlot[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const daysI = slotDaysExpanded(slots[i]);
      const daysJ = slotDaysExpanded(slots[j]);
      const commonDays = daysI.filter((d) => daysJ.includes(d));
      if (commonDays.length === 0) continue;
      const aRanges = rangesForSlot(slots[i].start, slots[i].end);
      const bRanges = rangesForSlot(slots[j].start, slots[j].end);
      const overlap = aRanges.some((a) => bRanges.some((b) => rangesOverlap(a, b)));
      if (overlap) out[slots[j].id] = 'Time slot overlaps with another slot.';
    }
  }
  return out;
}

function to12h(time: string): string {
  const [hStr, m] = time.split(':');
  const h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return time;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:${m ?? '00'} ${suffix}`;
}

function formatSlotSummary(slot: LocalSlot): string {
  const dayLabel = slot.day === 'all' ? 'Every day' : DAY_SHORT[slot.day] ?? slot.day;
  return `${dayLabel} | ${to12h(slot.start)} – ${to12h(slot.end)}`;
}

export default function MediaScheduler({ schedule, onChange, onValidityChange }: MediaSchedulerProps) {
  const [localSlots, setLocalSlots] = useState<LocalSlot[]>(() => deriveLocalSlots(schedule));

  const rangeErrors = useMemo(() => getRangeErrors(localSlots), [localSlots]);
  const overlapErrors = useMemo(() => getOverlapErrors(localSlots), [localSlots]);
  // Range errors take priority — an overlap check on a broken slot isn't meaningful yet.
  const slotErrors = useMemo(() => ({ ...overlapErrors, ...rangeErrors }), [overlapErrors, rangeErrors]);

  useEffect(() => {
    onValidityChange?.(Object.keys(rangeErrors).length === 0 && Object.keys(overlapErrors).length === 0);
  }, [rangeErrors, overlapErrors, onValidityChange]);

  const commit = (next: LocalSlot[]) => {
    setLocalSlots(next);
    onChange(next.flatMap(expandSlot));
  };

  const addSlot = () => commit([...localSlots, { id: nextSlotId(), day: 'all', start: '09:00', end: '17:00' }]);
  const removeSlot = (id: string) => commit(localSlots.filter((s) => s.id !== id));
  const updateSlot = (id: string, patch: Partial<LocalSlot>) => commit(localSlots.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  return (
    <div className="media-scheduler-root">
      {localSlots.length === 0 && (
        <div className="media-scheduler-empty-banner">
          <Icon name="all_inclusive" size={14} color={T3} />
          No schedule set — this media plays at all times
        </div>
      )}

      {localSlots.map((slot, index) => (
        <div key={slot.id} className="media-scheduler-slot-card">
          <div className="media-scheduler-slot-header">
            <span className="media-scheduler-slot-title">Slot {index + 1}</span>
            <button type="button" onClick={() => removeSlot(slot.id)} className="media-scheduler-remove-btn">
              <Icon name="close" size={16} color={T3} />
            </button>
          </div>

          <div style={{ marginBottom: 10 }}>
            <div className="media-scheduler-day-label">Day</div>
            <div className="media-scheduler-day-pills-row">
              <button
                type="button"
                onClick={() => updateSlot(slot.id, { day: 'all' })}
                className={`media-scheduler-day-pill ${slot.day === 'all' ? 'is-active' : ''}`}
              >
                All days
              </button>
              {WEEKDAYS.map((day) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => updateSlot(slot.id, { day })}
                  className={`media-scheduler-day-pill ${slot.day === day ? 'is-active' : ''}`}
                >
                  {DAY_SHORT[day]}
                </button>
              ))}
            </div>
          </div>

          <div className="media-scheduler-time-row">
            <div className="media-scheduler-time-col">
              <DisplayTimePicker label="Start" value={slot.start} onChange={(v) => updateSlot(slot.id, { start: v })} />
            </div>
            <span className="media-scheduler-time-arrow">→</span>
            <div className="media-scheduler-time-col">
              <DisplayTimePicker label="End" value={slot.end} onChange={(v) => updateSlot(slot.id, { end: v })} />
            </div>
          </div>

          {slotErrors[slot.id] && (
            <div className="media-scheduler-slot-error">{slotErrors[slot.id]}</div>
          )}
          <div className="media-scheduler-slot-summary">{formatSlotSummary(slot)}</div>
        </div>
      ))}

      <button
        type="button"
        onClick={addSlot}
        className="media-scheduler-add-btn"
      >
        <Icon name="add" size={16} color={ACCENT} />
        Add time slot
      </button>
    </div>
  );
}
